import {
  useGetCustomerNotificationsSettings,
  useUpdateCustomerNotificationsSettings,
} from "controller";
import {
  useGetMspResourceNotificationSettings,
  useGetMspUserNotificationSettings,
  useUpdateMspResourceNotificationSettings,
  useUpdateMspUserNotificationSettings,
} from "controller/hooks/control-center/notifications";
import { StatusFilterEditAction } from "./StatusFilterEditAction";
import {
  type StatusFilterEntry,
  TicketStatusSelectDrawer,
} from "./TicketStatusSelectDrawer";

// The only notify types the backend accepts `status_ids` on.
const TECHNICIAN_NOTIFY_TYPE = "ticket_status_update";
const CUSTOMER_NOTIFY_TYPE = "update_ticket";

const findEntry = (
  entries: StatusFilterEntry[] | undefined,
  notifyType: string,
) => entries?.find((entry) => entry.notify_type === notifyType);

interface DrawerProps {
  onClose: () => void;
}

const TechnicianStatusDrawer = ({ onClose }: DrawerProps) => {
  const { data, isFetching } = useGetMspResourceNotificationSettings();
  const { mutateAsync: updateSettings, isLoading: isSaving } =
    useUpdateMspResourceNotificationSettings();

  return (
    <TicketStatusSelectDrawer
      onClose={onClose}
      entry={findEntry(data?.result, TECHNICIAN_NOTIFY_TYPE)}
      isEntryFetching={isFetching}
      isSaving={isSaving}
      onSave={(body) =>
        updateSettings({ data: body, notificationType: TECHNICIAN_NOTIFY_TYPE })
      }
    />
  );
};

const CustomerDefaultStatusDrawer = ({ onClose }: DrawerProps) => {
  const { data, isFetching } = useGetMspUserNotificationSettings();
  const { mutateAsync: updateSettings, isLoading: isSaving } =
    useUpdateMspUserNotificationSettings();

  return (
    <TicketStatusSelectDrawer
      onClose={onClose}
      entry={findEntry(data?.result, CUSTOMER_NOTIFY_TYPE)}
      isEntryFetching={isFetching}
      isSaving={isSaving}
      onSave={(body) =>
        updateSettings({ data: body, notificationType: CUSTOMER_NOTIFY_TYPE })
      }
    />
  );
};

const CustomerStatusDrawer = ({
  onClose,
  customerId,
}: DrawerProps & { customerId: string }) => {
  const { data, isFetching } = useGetCustomerNotificationsSettings(customerId);
  const { mutateAsync: updateSettings, isLoading: isSaving } =
    useUpdateCustomerNotificationsSettings();

  return (
    <TicketStatusSelectDrawer
      onClose={onClose}
      entry={findEntry(data?.result, CUSTOMER_NOTIFY_TYPE)}
      isEntryFetching={isFetching}
      isSaving={isSaving}
      onSave={(body) =>
        updateSettings({
          data: body,
          notificationType: CUSTOMER_NOTIFY_TYPE,
          customerId,
        })
      }
    />
  );
};

/** Control Center → Notifications → For techs → "Ticket Status Updated". */
export const TicketStatusEditAction = () => (
  <StatusFilterEditAction
    renderDrawer={(onClose) => <TechnicianStatusDrawer onClose={onClose} />}
  />
);

/** Control Center → Notifications → For customers (MSP-wide customer default). */
export const CustomerDefaultTicketStatusEditAction = () => (
  <StatusFilterEditAction
    renderDrawer={(onClose) => <CustomerDefaultStatusDrawer onClose={onClose} />}
  />
);

/** Customer → Notifications tab (one customer's own settings). */
export const CustomerTicketStatusEditAction = ({
  customerId,
}: {
  customerId: string;
}) => (
  <StatusFilterEditAction
    renderDrawer={(onClose) => (
      <CustomerStatusDrawer onClose={onClose} customerId={customerId} />
    )}
  />
);
